import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { requerimientoService, dashboardService, bcvService } from '../services';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const Dashboard = () => {
  const {
    usuario,
    esPresidente,
    esAdministracion,
    esAnalistaAdministracion,
    esVp,
    esAnalista,
  } = useAuth();

  const [requerimientos, setRequerimientos] = useState([]);
  const [statsGeneral, setStatsGeneral] = useState(null);
  const [tasaBcv, setTasaBcv] = useState(null);
  const [cargando, setCargando] = useState(true);

  // Pagination & Search state
  const [busqueda, setBusqueda] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 10;

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setCargando(true);

      // Cargar tasa BCV para todos
      try {
        const tasaData = await bcvService.getTasaHoy();
        setTasaBcv(tasaData);
      } catch (err) {
        console.log('Tasa BCV no disponible para hoy:', err.message);
      }

      if (esPresidente() || esAdministracion() || esAnalistaAdministracion() || esVp()) {
        const promises = [dashboardService.getGeneral()];
        
        if (esVp()) {
          promises.push(requerimientoService.bandejaEntrada());
        } else {
          promises.push(requerimientoService.getAll());
        }

        const [statsData, reqsData] = await Promise.all(promises);
        setStatsGeneral(statsData);
        let listaReqs = reqsData.results || reqsData || [];
        
        if ((esAdministracion() || esAnalistaAdministracion()) && !esPresidente()) {
          listaReqs = listaReqs.filter(req => [
            'aprobado_vicepresidencia',
            'aprobado_vp_comercial',
            'aprobado_vp_operativa',
            'aprobado_vp_administrativa'
          ].includes(req.estado_actual));
        }
        
        if (esPresidente()) {
          // Filtrar por semana en curso para que el presidente no vea todos en el inicio
          const ahora = new Date();
          const diaDeSemana = ahora.getDay();
          const diff = ahora.getDate() - diaDeSemana + (diaDeSemana === 0 ? -6 : 1); // Lunes como primer día
          const inicioSemana = new Date(ahora.setDate(diff));
          inicioSemana.setHours(0, 0, 0, 0);
          
          listaReqs = listaReqs.filter(req => {
            if (!req.fecha_creacion) return false;
            return new Date(req.fecha_creacion) >= inicioSemana;
          });
        }
        
        setRequerimientos(listaReqs);
      } else if (esAnalista()) {
        const reqsData = await requerimientoService.bandejaEnviados();
        let listaReqs = reqsData.requerimientos || reqsData.results || reqsData || [];
        
        // Filtrar por semana en curso
        const ahora = new Date();
        const diaDeSemana = ahora.getDay();
        const diff = ahora.getDate() - diaDeSemana + (diaDeSemana === 0 ? -6 : 1); // Lunes como primer día
        const inicioSemana = new Date(ahora.setDate(diff));
        inicioSemana.setHours(0, 0, 0, 0);
        
        listaReqs = listaReqs.filter(req => {
          if (!req.fecha_creacion) return false;
          return new Date(req.fecha_creacion) >= inicioSemana;
        });

        setRequerimientos(listaReqs);
      }
    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
    } finally {
      setCargando(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colores = {
      elaborado: '#2196f3',
      por_revision: '#ff9800',
      aprobado_vp_comercial: '#4caf50',
      aprobado_vp_operativa: '#4caf50',
      rechazado_vp_comercial: '#f44336',
      rechazado_vp_operativa: '#f44336',
      recibido_vp_administrativa: '#00bcd4',
      aprobado_vp_administrativa: '#8bc34a',
      rechazado_vp_administrativa: '#f44336',
      pagado: '#9c27b0',
    };
    return colores[estado] || '#9e9e9e';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      elaborado: 'Elaborado',
      por_revision: 'Por Revisión',
      aprobado_vp_comercial: 'Aprobado VP Comercial',
      aprobado_vp_operativa: 'Aprobado VP Operativa',
      rechazado_vp_comercial: 'Rechazado VP Comercial',
      rechazado_vp_operativa: 'Rechazado VP Operativa',
      recibido_vp_administrativa: 'Recibido VP Admin',
      aprobado_vp_administrativa: 'Aprobado VP Admin',
      rechazado_vp_administrativa: 'Rechazado VP Admin',
      pagado: 'Pagado',
    };
    return labels[estado] || estado;
  };

  const getCriticidadBadge = (criticidad) => {
    const badges = {
      alta: { label: 'Alta 🔴', color: '#fff5f5', border: '#feb2b2', text: '#c53030' },
      media: { label: 'Media 🟡', color: '#fefcbf', border: '#faf089', text: '#b7791f' },
      baja: { label: 'Baja 🟢', color: '#f0fff4', border: '#9ae6b4', text: '#276749' }
    };
    const b = badges[criticidad];
    if (!b) return <span style={{ color: '#a0aec0', fontSize: '12px' }}>Pendiente</span>;
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: b.color,
        border: `1px solid ${b.border}`,
        color: b.text,
        whiteSpace: 'nowrap',
        display: 'inline-block'
      }}>
        {b.label}
      </span>
    );
  };

  const getRoleBadgeLabel = () => {
    if (esPresidente()) return 'Presidente';
    if (esAdministracion()) return 'Administración';
    if (esAnalistaAdministracion()) return 'Analista de Administración';
    if (esVp()) {
        const vpNombre = usuario?.vicepresidencia?.nombre || usuario?.vicepresidencia_nombre;
        return vpNombre ? `VP ${vpNombre}` : 'Vicepresidencia';
    }
    if (esAnalista()) return `Analista - ${usuario?.estado_asignado_nombre || 'Sin Región asignada'}`;
    return usuario?.role || 'Usuario';
  };

  if (cargando) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Cargando panel de control...</p>
      </div>
    );
  }

  // --- RENDERS ---

  const renderPresidentStats = () => {
    if (!statsGeneral) return null;

    return (
      <>
        {/* Widgets Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon primary">📋</div>
            <div className="stat-content">
              <h3>Total Requerimientos</h3>
              <p className="stat-number">{statsGeneral.total_requerimientos}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">💸</div>
            <div className="stat-content">
              <h3>Monto Total Solicitado</h3>
              <p className="stat-number">
                {parseFloat(statsGeneral.monto_total_solicitado || 0).toLocaleString('es-VE')} Bs
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon info">⏱️</div>
            <div className="stat-content">
              <h3>Requerimientos (Últimos 30 días)</h3>
              <p className="stat-number">{statsGeneral.recientes_30_dias}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon warning">💵</div>
            <div className="stat-content">
              <h3>Tasa BCV del Día</h3>
              <p className="stat-number">
                {tasaBcv ? `${parseFloat(tasaBcv.tasa).toFixed(4)} Bs/$` : 'No cargada'}
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Widgets Column (Geographical & Status charts) */}
        <div className="dashboard-widgets-grid">
          {/* Geographical State counts */}
          <div className="dashboard-widget-box">
            <div className="widget-header">
              <h3>Requerimientos por Estado</h3>
            </div>
            <div className="widget-body">
              {statsGeneral.por_estado_venezolano && statsGeneral.por_estado_venezolano.length > 0 ? (
                <div className="state-progress-list">
                  {statsGeneral.por_estado_venezolano.map((item, idx) => {
                    const totalReqs = statsGeneral.total_requerimientos || 1;
                    const percent = Math.round((item.cantidad / totalReqs) * 100);
                    return (
                      <div key={idx} className="state-progress-item">
                        <div className="state-progress-label">
                          <span className="state-name">{item.ubicacion_estado__nombre || 'N/A'}</span>
                          <span className="state-count">{item.cantidad} reqs ({percent}%)</span>
                        </div>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${percent}%`, backgroundColor: `hsl(${210 - (percent * 1.5)}, 70%, 45%)` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-message-subtle">No hay datos por estado geográfico.</p>
              )}
            </div>
          </div>

          {/* Ticket Status & VP breakdown */}
          <div className="dashboard-widget-column">
            <div className="dashboard-widget-box">
              <div className="widget-header">
                <h3>Por Estatus de Requerimiento</h3>
              </div>
              <div className="widget-body">
                <div className="status-badge-list">
                  {Object.entries(statsGeneral.por_estado || {}).map(([key, val]) => {
                    if (val === 0) return null;
                    return (
                      <div key={key} className="status-count-item">
                        <span
                          className="estado-badge-dot"
                          style={{ backgroundColor: getEstadoColor(key) }}
                        />
                        <span className="status-label">{getEstadoLabel(key)}</span>
                        <span className="status-val">{val}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {!esVp() && (
              <div className="dashboard-widget-box">
                <div className="widget-header">
                  <h3>Por Vicepresidencia</h3>
                </div>
              <div className="widget-body">
                <div className="vp-counts-list">
                  {statsGeneral.por_vicepresidencia && statsGeneral.por_vicepresidencia.length > 0 ? (
                    statsGeneral.por_vicepresidencia.map((vp, idx) => (
                      <div key={idx} className="vp-count-row">
                        <span className="vp-name">{vp.vicepresidencia__nombre || 'Sin VP'}</span>
                        <span className="vp-badge">{vp.cantidad} requerimientos</span>
                      </div>
                    ))
                  ) : (
                    <p className="empty-message-subtle">No hay datos por vicepresidencia.</p>
                  )}
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderVpStats = () => {
    return (
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon primary">📥</div>
          <div className="stat-content">
            <h3>Requerimientos en Bandeja</h3>
            <p className="stat-number">{requerimientos.length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">💵</div>
          <div className="stat-content">
            <h3>Tasa BCV del Día</h3>
            <p className="stat-number">
              {tasaBcv ? `${parseFloat(tasaBcv.tasa).toFixed(4)} Bs/$` : 'No cargada'}
            </p>
          </div>
        </div>
        <div className="stat-card clickable-card">
          <Link to="/gerencias" className="card-link-wrapper">
            <div className="stat-icon info">🏢</div>
            <div className="stat-content">
              <h3>Mis Gerencias</h3>
              <p className="stat-text-link">Administrar gerencias</p>
            </div>
          </Link>
        </div>
      </div>
    );
  };

  const renderAnalistaStats = () => {
    const reqs = Array.isArray(requerimientos) ? requerimientos : [];
    const totalEnviados = reqs.length;
    const aprobados = reqs.filter(r => ['pagado', 'aprobado_vp_administrativa'].includes(r.estado_actual)).length;
    const rechazados = reqs.filter(r => ['rechazado_vp_comercial', 'rechazado_vp_operativa', 'rechazado_vp_administrativa'].includes(r.estado_actual)).length;

    return (
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon primary">📤</div>
          <div className="stat-content">
            <h3>Total Enviados</h3>
            <p className="stat-number">{totalEnviados}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success">✅</div>
          <div className="stat-content">
            <h3>Aprobados</h3>
            <p className="stat-number">{aprobados}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon danger">❌</div>
          <div className="stat-content">
            <h3>Rechazados</h3>
            <p className="stat-number">{rechazados}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">💵</div>
          <div className="stat-content">
            <h3>Tasa BCV del Día</h3>
            <p className="stat-number">
              {tasaBcv ? `${parseFloat(tasaBcv.tasa).toFixed(4)} Bs/$` : 'No cargada'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header-container">
        <div className="welcome-banner">
          <span className="dashboard-welcome">Panel de Control</span>
          <h2>Hola, {usuario?.first_name || usuario?.username}</h2>
          <span className="user-role-badge">{getRoleBadgeLabel()}</span>
        </div>

        {/* Acciones principales */}
        <div className="dashboard-actions">
          {(esAnalista() && !esAnalistaAdministracion()) && (
            <Link to="/requerimientos/nuevo" className="btn btn-primary">
              <span className="btn-icon">✚</span> Nuevo Requerimiento
            </Link>
          )}
          {(esVp() || esAdministracion() || esAnalistaAdministracion()) && (
            <Link to="/bandeja-entrada" className="btn btn-primary" style={{ marginLeft: '10px', display: 'inline-flex', alignItems: 'center' }}>
              📥 Bandeja de Entrada
              {requerimientos.length > 0 && (
                <span style={{
                  marginLeft: '8px', 
                  backgroundColor: '#dc2626', color: 'white', borderRadius: '12px', 
                  padding: '2px 8px', fontSize: '12px', fontWeight: 'bold'
                }}>
                  {requerimientos.length}
                </span>
              )}
            </Link>
          )}
          {esPresidente() && (
            <Link to="/admin" className="btn btn-secondary" style={{ marginLeft: '10px' }}>
              ⚙ Administrar Sistema
            </Link>
          )}
        </div>
      </div>

      {/* Renderizar estadísticas según el rol */}
      {(esPresidente() || esAdministracion() || esAnalistaAdministracion() || esVp()) && renderPresidentStats()}
      {esAnalista() && renderAnalistaStats()}

      {/* Tabla de Requerimientos Recientes/Pendientes (Solo para Presidente y Analistas) */}
      {(esPresidente() || esAnalista()) && (
        <div className="table-card-container">
          <div className="table-card-header">
            <h3>
              {esPresidente()
                ? 'Últimos Requerimientos en el Sistema (Semana en Curso)'
                : 'Requerimientos Creados (Semana en Curso)'}
            </h3>
            <span className="table-count-pill">{requerimientos.length} EN TOTAL</span>
          </div>

          {/* Buscador simple (solo analistas o si hay muchos reqs) */}
          {requerimientos.length > 0 && (
            <div className="table-search-bar">
              <input
                type="text"
                placeholder="🔍 Buscar por código, solicitante, o estatus..."
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPaginaActual(1);
                }}
                className="table-search-input"
              />
            </div>
          )}

          {requerimientos.length === 0 ? (
            <div className="empty-state-container">
              <div className="empty-state-icon">📥</div>
              <h4>No hay requerimientos recientes</h4>
              <p>No se encontraron requerimientos para mostrar en este momento.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Solicitante</th>
                    <th>Ubicación / Estado</th>
                    {esPresidente() && <th>VP / Gerencia</th>}
                    <th>Criticidad</th>
                    <th>Monto (Bs)</th>
                    <th>Estatus</th>
                    <th>Fecha</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtrados = requerimientos.filter(req => {
                      const texto = `${req.correlativo || req.id} ${req.solicitante_cedula} ${req.solicitante_nombre} ${req.solicitante_apellido} ${getEstadoLabel(req.estado_actual)}`.toLowerCase();
                      return texto.includes(busqueda.toLowerCase());
                    });
                    const indiceUltimo = paginaActual * itemsPorPagina;
                    const indicePrimero = indiceUltimo - itemsPorPagina;
                    const reqsPaginados = filtrados.slice(indicePrimero, indiceUltimo);
                    
                    if (filtrados.length === 0) {
                      return <tr><td colSpan="10" className="text-center" style={{padding:'20px'}}>No se encontraron coincidencias</td></tr>;
                    }

                    return reqsPaginados.map((req) => (
                      <tr key={req.id}>
                        <td>
                          <strong>{req.correlativo || req.id}</strong>
                        </td>
                        <td>
                          <div className="solicitante-info">
                            <span className="solicitante-nombre">
                              {req.solicitante_nombre} {req.solicitante_apellido}
                            </span>
                            <span className="solicitante-cedula">C.I. {req.solicitante_cedula}</span>
                          </div>
                        </td>
                        <td>{req.ubicacion_estado_nombre || 'N/A'}</td>
                        {esPresidente() && (
                          <td>
                            <div className="jerarquia-info">
                              <span className="vp-nombre">{req.vicepresidencia_nombre || 'N/A'}</span>
                              <span className="gerencia-nombre">{req.gerencia_nombre || 'N/A'}</span>
                            </div>
                          </td>
                        )}
                        <td>{getCriticidadBadge(req.criticidad)}</td>
                        <td>{parseFloat(req.monto_bs).toLocaleString('es-VE')} Bs</td>
                        <td>
                          <span
                            className="estado-badge"
                            style={{ backgroundColor: getEstadoColor(req.estado_actual), color: '#ffffff' }}
                          >
                            {req.estado_display || getEstadoLabel(req.estado_actual)}
                          </span>
                        </td>
                        <td>{new Date(req.fecha_creacion).toLocaleDateString('es-VE')}</td>
                        <td className="text-right">
                          <Link to={`/requerimientos/${req.correlativo || req.id}`} className="btn-link-action">
                            Ver detalle →
                          </Link>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
          
          {requerimientos.length > 0 && (
            <div className="table-pagination-bar">
              <button 
                onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                disabled={paginaActual === 1}
                className="table-pagination-button"
              >
                Anterior
              </button>
              <span className="table-pagination-info">
                Página {paginaActual}
              </span>
              <button 
                onClick={() => {
                  const filtrados = requerimientos.filter(req => {
                      const texto = `${req.correlativo || req.id} ${req.solicitante_cedula} ${req.solicitante_nombre} ${req.solicitante_apellido} ${getEstadoLabel(req.estado_actual)}`.toLowerCase();
                      return texto.includes(busqueda.toLowerCase());
                  });
                  if (paginaActual * itemsPorPagina < filtrados.length) {
                      setPaginaActual(prev => prev + 1);
                  }
                }}
                disabled={(() => {
                   const filtrados = requerimientos.filter(req => {
                      const texto = `${req.correlativo || req.id} ${req.solicitante_cedula} ${req.solicitante_nombre} ${req.solicitante_apellido} ${getEstadoLabel(req.estado_actual)}`.toLowerCase();
                      return texto.includes(busqueda.toLowerCase());
                   });
                   return paginaActual * itemsPorPagina >= filtrados.length;
                })()}
                className="table-pagination-button"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
